import { useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import { Outlet, useLocation } from 'react-router-dom';
import styled from '@mui/material/styles/styled';
import CssBaseline from '@mui/material/CssBaseline';
import { useDispatch, useSelector } from 'react-redux';

import Topbar from 'layouts/Topbar';
import Sidebar from 'layouts/Sidebar';
import DrawerContent from './DrawerContent';
import LogoutModal from 'components/auth/LogoutModal';

import Auth from 'utils/Auth';
import constants from 'app/config/constants';
import { setIsDrawerOpen } from 'app/features/app/appSlice';
import { getLoggedInUser } from 'app/features/auth/authSaga';
import { getUserRolesAndPermissions } from 'app/features/roles-and-permissions/rolesAndPermissionsSaga';
import { getSidebarIconForMenu } from 'layouts/menuSidebarIcons';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

function AppLayout() {
  const dispatch = useDispatch();
  const location = useLocation();

  const { isDrawerOpen: open } = useSelector((state) => state.app);

  const menus = useSelector(
    (state) => state.rolesAndPermissions.userRolesAndPermissions.response?.data || [],
  );

  const handleDrawerClose = () => dispatch(setIsDrawerOpen(false));
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (!user) {
      dispatch(getLoggedInUser());
    }
    dispatch(getUserRolesAndPermissions());
    sessionStorage.clear(constants.INTENDED_PATH);
  }, [dispatch]);

  const sidebarItems = useMemo(() => {
    if (!Array.isArray(menus)) return [];

    return menus
      .filter((menu) => menu.menuType === 'MENU' && !menu.isHidden)
      .map((menu) => {
        const subMenus = menu.children?.filter(
          (child) => child.menuType === 'SUB_MENU' && !child.isHidden,
        );

        if (!subMenus?.length) {
          return {
            type: 'link',
            name: menu.displayName,
            icon: getSidebarIconForMenu(menu),
            path: menu.url || '/',
            isActive: location.pathname === menu.url,
          };
        }

        return {
          type: 'dropdown',
          name: menu.displayName,
          icon: getSidebarIconForMenu(menu),
          isActive: subMenus.some((sub) => location.pathname.startsWith(sub.url)),
          children: subMenus.map((sub) => ({
            type: 'link',
            name: sub.displayName,
            path: sub.url,
            isActive: location.pathname === sub.url,
          })),
        };
      });
  }, [menus, location.pathname]);

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: (theme) => theme.palette.grey[100],
        overflowX: 'hidden',
      }}
    >
      <CssBaseline />
      <Topbar />
      <Sidebar
        open={open}
        handleDrawerClose={handleDrawerClose}
        appBar={<DrawerContent open={open} />}
        sidebarItems={sidebarItems}
      />
      <Box component="main" sx={{ flexGrow: 1, paddingY: 3 }}>
        <DrawerHeader />
        <Outlet />
      </Box>
      <LogoutModal />
    </Box>
  );
}

export default AppLayout;
